import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { saveChildInfo, ChildInfoData } from "../redux/Action";
import { RootState } from "../redux/Store";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";

interface ChildInfoFormData {
  guardianName: string;
  guardianPhoneCountryCode: string;
  guardianPhoneNumber: string;
  childName: string;
  gender: "male" | "female" | "";
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  city: string;
  pincode: string;
}

const ChildInfo: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const existingChildInfo = useSelector(
    (state: RootState) => (state as any).childInfo
  );

  const [formData, setFormData] = useState<ChildInfoFormData>({
    guardianName: "",
    guardianPhoneCountryCode: "+91",
    guardianPhoneNumber: "",
    childName: "",
    gender: "",
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    city: "",
    pincode: "",
  });

  useEffect(() => {
    if (existingChildInfo) {
      let countryCode = "+91";
      let phoneNumber = "";
      if (existingChildInfo.guardianPhone) {
        try {
          const parsed = parsePhoneNumber(existingChildInfo.guardianPhone);
          if (parsed) {
            countryCode = `+${parsed.countryCallingCode}`;
            phoneNumber = parsed.nationalNumber as string;
          } else {
            phoneNumber = existingChildInfo.guardianPhone;
          }
        } catch (e) {
          phoneNumber = existingChildInfo.guardianPhone;
        }
      }

      const dob = existingChildInfo.dateOfBirth ? new Date(existingChildInfo.dateOfBirth) : null;

      setFormData({
        guardianName: existingChildInfo.guardianName || "",
        guardianPhoneCountryCode: countryCode,
        guardianPhoneNumber: phoneNumber,
        childName: existingChildInfo.childName || "",
        gender: existingChildInfo.gender || "",
        dobDay: dob ? String(dob.getDate()) : "",
        dobMonth: dob ? String(dob.getMonth() + 1) : "",
        dobYear: dob ? String(dob.getFullYear()) : "",
        city: existingChildInfo.city || "",
        pincode: existingChildInfo.pincode || "",
      });
    }
  }, [existingChildInfo]);

  const [
    errors,
    setErrors,
  ] = useState<Partial<Record<keyof ChildInfoFormData, string>>>({});

  const handleInputChange = (
    field: keyof ChildInfoFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validatePhoneNumber = (
    countryCode: string,
    phoneNumber: string
  ): boolean => {
    if (!phoneNumber.trim()) return false;
    try {
      return isValidPhoneNumber(countryCode + phoneNumber);
    } catch (error) {
      return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ChildInfoFormData, string>> = {};

    if (!formData.guardianName.trim()) {
      newErrors.guardianName = "Guardian name is required";
    }

    if (!formData.guardianPhoneNumber.trim()) {
      newErrors.guardianPhoneNumber = "Phone number is required";
    } else if (
      !validatePhoneNumber(
        formData.guardianPhoneCountryCode,
        formData.guardianPhoneNumber
      )
    ) {
      newErrors.guardianPhoneNumber =
        "Please enter a valid phone number.";
    }

    if (!formData.childName.trim()) {
      newErrors.childName = "Child name is required";
    }

    if (!formData.gender) {
      newErrors.gender = "Please select gender";
    }

    if (!formData.dobDay || !formData.dobMonth || !formData.dobYear) {
      newErrors.dobDay = "Date of birth is required";
    } else {
      const birthDate = new Date(Number(formData.dobYear), Number(formData.dobMonth) - 1, Number(formData.dobDay));
      const today = new Date();
      const ageInMonths =
        (today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44);

      if (ageInMonths < 16 || ageInMonths > 30) {
        newErrors.dobDay =
          "Child must be between 16-30 months old for this assessment";
      }
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.pincode.trim())) {
      newErrors.pincode = "Please enter a valid 6-digit pincode";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      const dateOfBirth = `${formData.dobYear}-${String(formData.dobMonth).padStart(2, '0')}-${String(formData.dobDay).padStart(2, '0')}`;
      const childInfo: ChildInfoData = {
        guardianName: formData.guardianName.trim(),
        guardianPhone: `${formData.guardianPhoneCountryCode}${formData.guardianPhoneNumber.trim()}`,
        childName: formData.childName.trim(),
        gender: formData.gender as "male" | "female",
        dateOfBirth: dateOfBirth,
        city: formData.city.trim(),
        pincode: formData.pincode.trim(),
      };

      dispatch(saveChildInfo(childInfo));
      navigate("/question/1");
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i -1);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-indigo-100 via-purple-50 to-indigo-200 flex flex-col items-center justify-center py-8 px-2">
      <div className="w-full max-w-5xl bg-white/80 rounded-2xl shadow-2xl p-6 md:p-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-600 bg-clip-text text-transparent mb-2">
            Child Information
          </h1>
          <p className="text-gray-600 text-lg">
            Please provide the following information to begin the M-CHAT-R
            Followup assessment
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Guardian Information */}
          <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-100 rounded-xl p-6 border border-indigo-200">
            <h2 className="text-xl font-semibold text-indigo-800 mb-6 flex items-center">
              <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                1
              </span>
              Guardian Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <label
                  htmlFor="guardianName"
                  className="block text-sm font-semibold text-indigo-700 mb-2 text-left"
                >
                  Guardian Name *
                </label>
                <input
                  type="text"
                  id="guardianName"
                  value={formData.guardianName}
                  onChange={(e) =>
                    handleInputChange("guardianName", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                    errors.guardianName
                      ? "border-red-500 bg-red-50"
                      : "border-indigo-300 bg-white/70"
                  }`}
                  placeholder="Enter guardian's full name"
                />
                {errors.guardianName && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠</span>
                    {errors.guardianName}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="guardianPhone"
                  className="block text-sm font-semibold text-indigo-700 mb-2 text-left"
                >
                  Phone Number *
                </label>
                <div className="flex">
                  <select
                    onChange={(e) =>
                      handleInputChange("guardianPhoneCountryCode", e.target.value)
                    }
                    value={formData.guardianPhoneCountryCode}
                    className="px-2 py-3 border border-r-0 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors border-indigo-300 bg-white/70"
                  >
                    <option value="+91">IN (+91)</option>
                    <option value="+1">US (+1)</option>
                    {/* Add other country codes as needed */}
                  </select>
                  <input
                    type="tel"
                    id="guardianPhone"
                    value={formData.guardianPhoneNumber}
                    onChange={(e) =>
                      handleInputChange(
                        "guardianPhoneNumber",
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    className={`w-full px-4 py-3 border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                      errors.guardianPhoneNumber
                        ? "border-red-500 bg-red-50"
                        : "border-indigo-300 bg-white/70"
                    }`}
                    placeholder="9876543210"
                  />
                </div>
                {errors.guardianPhoneNumber && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠</span>
                    {errors.guardianPhoneNumber}
                  </p>
                )}
                <p className="text-xs text-indigo-600 mt-2 text-left">
                  Enter Phone Number.
                </p>
              </div>
            </div>
          </div>

          {/* Child Information */}
          <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-100 rounded-xl p-6 border border-purple-200">
            <h2 className="text-xl font-semibold text-purple-800 mb-6 flex items-center">
              <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                2
              </span>
              Child Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <label
                  htmlFor="childName"
                  className="block text-sm font-semibold text-purple-700 mb-2 text-left"
                >
                  Child's Name *
                </label>
                <input
                  type="text"
                  id="childName"
                  value={formData.childName}
                  onChange={(e) =>
                    handleInputChange("childName", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                    errors.childName
                      ? "border-red-500 bg-red-50"
                      : "border-purple-300 bg-white/70"
                  }`}
                  placeholder="Enter child's full name"
                />
                {errors.childName && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠</span>
                    {errors.childName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-purple-700 mb-2 text-left">
                  Gender *
                </label>
                <div className="flex space-x-6 justify-left bg-white/70 rounded-lg p-3 gap-10 border border-purple-300">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === "male"}
                      onChange={(e) =>
                        handleInputChange("gender", e.target.value)
                      }
                      className="mr-2 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-purple-700 font-medium">Male</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === "female"}
                      onChange={(e) =>
                        handleInputChange("gender", e.target.value)
                      }
                      className="mr-2 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-purple-700 font-medium">
                      Female
                    </span>
                  </label>
                </div>
                {errors.gender && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠</span>
                    {errors.gender}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-6">
              <div>
                <label
                  htmlFor="dateOfBirth"
                  className="block text-sm font-semibold text-purple-700 mb-2 text-left"
                >
                  Date of Birth *
                </label>
                <div className="grid grid-cols-3 gap-2">
                <select
                  name="dobDay"
                  value={formData.dobDay}
                  onChange={(e) => handleInputChange("dobDay", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                    errors.dobDay ? "border-red-500 bg-red-50" : "border-purple-300 bg-white/70"
                  }`}
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <select
                  name="dobMonth"
                  value={formData.dobMonth}
                  onChange={(e) => handleInputChange("dobMonth", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                    errors.dobDay ? "border-red-500 bg-red-50" : "border-purple-300 bg-white/70"
                  }`}
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <select
                  name="dobYear"
                  value={formData.dobYear}
                  onChange={(e) => handleInputChange("dobYear", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                    errors.dobDay ? "border-red-500 bg-red-50" : "border-purple-300 bg-white/70"
                  }`}
                >
                  <option value="">Year</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                </div>
                {errors.dobDay && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠</span>
                    {errors.dobDay}
                  </p>
                )}
                <p className="text-xs text-purple-600 mt-2 text-left">
                  Child must be between 16-30 months old
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="city"
                    className="block text-sm font-semibold text-purple-700 mb-2 text-left"
                  >
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                      errors.city
                        ? "border-red-500 bg-red-50"
                        : "border-purple-300 bg-white/70"
                    }`}
                    placeholder="Enter city"
                  />
                  {errors.city && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <span className="mr-1">⚠</span>
                      {errors.city}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="pincode"
                    className="block text-sm font-semibold text-purple-700 mb-2 text-left"
                  >
                    Pincode *
                  </label>
                  <input
                    type="text"
                    id="pincode"
                    value={formData.pincode}
                    onChange={(e) =>
                      handleInputChange(
                        "pincode",
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    maxLength={6}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                      errors.pincode
                        ? "border-red-500 bg-red-50"
                        : "border-purple-300 bg-white/70"
                    }`}
                    placeholder="Enter 6-digit pincode"
                  />
                  {errors.pincode && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <span className="mr-1">⚠</span>
                      {errors.pincode}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-6">
            <button
              type="button"
              onClick={() => navigate("/home")}
              className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Back to Home
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Start Assessment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChildInfo;
